/**
 * Gantt charts: reading a task list, scheduling it, checking its dependencies, and
 * laying it out on a day, week or month scale.
 *
 * A task is one line: `task, start, end or duration, group, progress %, depends on`.
 * Dates are ISO (`2027-01-11`) because a Gantt chart that reads 03/04 as March or April
 * depending on who opens it is worse than none. An end date is inclusive — a task from
 * Monday to Friday lasts five days — and is stored as the first day after it, so a
 * task's length is `end − start`. A duration of 0 makes a milestone.
 *
 * Dependencies are finish-to-start: a task that depends on another should begin no
 * earlier than the day after that one ends. The chart draws the arrow either way and
 * the widget says which tasks break the rule.
 */

import {
  CHART_PADDING,
  layoutTitle,
  type Rect,
  type TitleBlock,
} from "@/lib/tool-kit/charts/layout";
import { detectDelimiter, splitCells } from "@/lib/tool-kit/charts/parse";
import { estimateTextWidth, truncateText } from "@/lib/tool-kit/charts/text";
import {
  civilDate,
  formatIsoDate,
  isWeekend,
  monthIndex,
  monthStart,
  parseIsoDate,
  startOfWeek,
} from "@/lib/tool-kit/diagrams/dates";
import { toCsv } from "@/lib/tool-kit/research-design/matrix";

/* ----------------------------------------------------------------- parsing */

export interface GanttTask {
  /** 1-based position among the tasks — what "depends on" numbers refer to. */
  number: number;
  line: number;
  name: string;
  group: string;
  /** First day. */
  start: number;
  /** First day after the task; equal to `start` for a milestone. */
  end: number;
  milestone: boolean;
  /** 0–100, or null when not given. */
  progress: number | null;
  /** Task numbers this one waits for. */
  depends: number[];
}

export type GanttProblemKind =
  | "noName"
  | "badStart"
  | "badEnd"
  | "endBeforeStart"
  | "badProgress";

export interface GanttProblem {
  line: number;
  kind: GanttProblemKind;
  text: string;
}

export type DependencyIssue =
  | { kind: "unknown"; task: number; reference: string }
  | { kind: "self"; task: number }
  | { kind: "early"; task: number; on: number }
  | { kind: "cycle"; tasks: number[] };

export interface GanttData {
  tasks: GanttTask[];
  problems: GanttProblem[];
  /** The first line named the columns. */
  header: boolean;
  dependencyIssues: DependencyIssue[];
}

const LINE_BREAK = /\r\n|\r|\n/;
const DURATION = /^(\d{1,4})\s*(d|days?|w|wks?|weeks?)?$/i;
const WEEK_UNIT = /^w/i;
const PERCENT = /^(\d{1,3}(?:[.,]\d+)?)\s*%?$/;
const REFERENCE_SPLIT = /[\s+&/]+/;
const INTEGER = /^\d+$/;

/** The next Monday-to-Friday day on or after `day`. */
export function nextWorkingDay(day: number): number {
  let next = day;
  while (isWeekend(next)) {
    next += 1;
  }
  return next;
}

/** The first day after `count` working days starting on `start`. */
export function addWorkingDays(start: number, count: number): number {
  let day = start;
  let done = 0;
  while (done < count) {
    if (!isWeekend(day)) {
      done += 1;
    }
    day += 1;
  }
  return day;
}

/** Working days from `start` up to (not including) `end`. */
export function workingDaysBetween(start: number, end: number): number {
  let count = 0;
  for (let day = start; day < end; day++) {
    if (!isWeekend(day)) {
      count += 1;
    }
  }
  return count;
}

interface RawTask {
  task: Omit<GanttTask, "depends">;
  references: string[];
}

function readEnd(
  cell: string,
  start: number,
  workingDays: boolean
): { end: number; milestone: boolean } | "badEnd" | "endBeforeStart" {
  const date = parseIsoDate(cell);
  if (date !== null) {
    return date < start
      ? "endBeforeStart"
      : { end: date + 1, milestone: false };
  }
  const match = DURATION.exec(cell.trim());
  if (!match) {
    return "badEnd";
  }
  const amount = Number(match[1]);
  if (amount === 0) {
    return { end: start, milestone: true };
  }
  const week = WEEK_UNIT.test(match[2] ?? "");
  if (workingDays) {
    return {
      end: addWorkingDays(start, amount * (week ? 5 : 1)),
      milestone: false,
    };
  }
  return { end: start + amount * (week ? 7 : 1), milestone: false };
}

function readProgress(cell: string): number | null | "bad" {
  const text = cell.trim();
  if (text === "") {
    return null;
  }
  const match = PERCENT.exec(text);
  if (!match) {
    return "bad";
  }
  const value = Number(match[1].replace(",", "."));
  return value > 100 ? "bad" : value;
}

function readTask(
  cells: string[],
  line: number,
  number: number,
  workingDays: boolean
): RawTask | GanttProblemKind {
  const [
    name = "",
    startCell = "",
    endCell = "",
    group = "",
    progressCell = "",
  ] = cells;
  if (name === "") {
    return "noName";
  }
  const written = parseIsoDate(startCell);
  if (written === null) {
    return "badStart";
  }
  const start = workingDays ? nextWorkingDay(written) : written;
  const end = readEnd(endCell, start, workingDays);
  if (typeof end === "string") {
    return end;
  }
  const progress = readProgress(progressCell);
  if (progress === "bad") {
    return "badProgress";
  }
  return {
    task: {
      number,
      line,
      name,
      group,
      start,
      end: end.end,
      milestone: end.milestone,
      progress,
    },
    references: cells.slice(5).filter((cell) => cell !== ""),
  };
}

/** The task numbers one "depends on" cell names: a task's name, or numbers and names. */
function readReferences(
  cell: string,
  byName: ReadonlyMap<string, number>,
  count: number
): { numbers: number[]; unknown: string[] } {
  const named = byName.get(cell.toLowerCase());
  if (named !== undefined) {
    return { numbers: [named], unknown: [] };
  }
  const numbers: number[] = [];
  const unknown: string[] = [];
  for (const token of cell.split(REFERENCE_SPLIT).filter(Boolean)) {
    const value = INTEGER.test(token)
      ? Number(token)
      : byName.get(token.toLowerCase());
    if (value === undefined || value < 1 || value > count) {
      unknown.push(token);
    } else {
      numbers.push(value);
    }
  }
  return { numbers, unknown };
}

function resolveReferences(raw: readonly RawTask[]): {
  tasks: GanttTask[];
  issues: DependencyIssue[];
} {
  const byName = new Map(
    raw.map((r) => [r.task.name.toLowerCase(), r.task.number])
  );
  const issues: DependencyIssue[] = [];
  const tasks = raw.map(({ task, references }) => {
    const depends: number[] = [];
    for (const cell of references) {
      const { numbers, unknown } = readReferences(cell, byName, raw.length);
      for (const reference of unknown) {
        issues.push({ kind: "unknown", task: task.number, reference });
      }
      for (const value of numbers) {
        if (value === task.number) {
          issues.push({ kind: "self", task: task.number });
        } else if (!depends.includes(value)) {
          depends.push(value);
        }
      }
    }
    return { ...task, depends };
  });
  return { tasks, issues };
}

/** Tasks that wait on themselves through a chain of dependencies, once per cycle. */
function findCycles(tasks: readonly GanttTask[]): number[][] {
  const state = new Map<number, "visiting" | "done">();
  const cycles: number[][] = [];
  const byNumber = new Map(tasks.map((task) => [task.number, task]));
  const visit = (number: number, path: number[]) => {
    const seen = state.get(number);
    if (seen === "done") {
      return;
    }
    if (seen === "visiting") {
      cycles.push(path.slice(path.indexOf(number)));
      return;
    }
    state.set(number, "visiting");
    for (const next of byNumber.get(number)?.depends ?? []) {
      visit(next, [...path, number]);
    }
    state.set(number, "done");
  };
  for (const task of tasks) {
    visit(task.number, []);
  }
  return cycles;
}

/** Dependencies that are broken: unknown, on itself, circular, or starting too early. */
function checkDependencies(tasks: readonly GanttTask[]): DependencyIssue[] {
  const byNumber = new Map(tasks.map((task) => [task.number, task]));
  const issues: DependencyIssue[] = [];
  for (const task of tasks) {
    for (const on of task.depends) {
      const before = byNumber.get(on);
      if (before && task.start < before.end) {
        issues.push({ kind: "early", task: task.number, on });
      }
    }
  }
  for (const cycle of findCycles(tasks)) {
    issues.push({ kind: "cycle", tasks: cycle });
  }
  return issues;
}

/**
 * Read the task box. The first line is a header when its start column is not a date.
 * With `workingDays`, durations count Monday to Friday and a start on a weekend moves to
 * the Monday after.
 */
export function parseGantt(
  text: string,
  { workingDays }: { workingDays: boolean }
): GanttData {
  const lines = text
    .split(LINE_BREAK)
    .map((raw, index) => ({ raw, line: index + 1 }))
    .filter(({ raw }) => raw.trim() !== "");
  const delimiter = detectDelimiter(lines.map(({ raw }) => raw));
  const rows = lines.map(({ raw, line }) => ({
    cells: splitCells(raw, delimiter === " " ? "," : delimiter),
    line,
    raw: raw.trim(),
  }));
  const header =
    rows.length > 0 && parseIsoDate(rows[0].cells[1] ?? "") === null;
  const raw: RawTask[] = [];
  const problems: GanttProblem[] = [];
  for (const row of header ? rows.slice(1) : rows) {
    const read = readTask(row.cells, row.line, raw.length + 1, workingDays);
    if (typeof read === "string") {
      problems.push({ line: row.line, kind: read, text: row.raw });
    } else {
      raw.push(read);
    }
  }
  const { tasks, issues } = resolveReferences(raw);
  return {
    tasks,
    problems,
    header,
    dependencyIssues: [...issues, ...checkDependencies(tasks)],
  };
}

/** The tasks as CSV with ISO dates and inclusive end dates — what "Export CSV" saves. */
/**
 * The tasks in the input's own columns — ISO dates, inclusive end dates, a 0 for each
 * milestone — so a copy pastes into a spreadsheet and an export imports back unchanged.
 */
export function ganttRows(
  tasks: readonly GanttTask[]
): (string | number | null)[][] {
  return tasks.map((task) => [
    task.name,
    formatIsoDate(task.start),
    task.milestone ? "0" : formatIsoDate(task.end - 1),
    task.group,
    task.progress,
    task.depends.join(" "),
  ]);
}

/** The tasks as CSV — what "Export CSV" saves. */
export function ganttCsv(
  tasks: readonly GanttTask[],
  head: readonly string[]
): string {
  return toCsv([head, ...ganttRows(tasks)]);
}

/* ------------------------------------------------------------------ layout */

export type GanttScale = "day" | "week" | "month";

/**
 * Header text, written by the widget in the reader's language: a year, a month with its
 * year (above days and weeks), a month alone (under its year) or a day of the month.
 * `room` is the width available.
 */
export type GanttHeaderUnit = "year" | "month" | "monthOnly" | "day";

export type GanttHeaderLabel = (
  unit: GanttHeaderUnit,
  day: number,
  room: number
) => string;

export interface GanttLayoutInput {
  tasks: readonly GanttTask[];
  scale: GanttScale;
  title: string;
  weekends: boolean;
  headerLabel: GanttHeaderLabel;
}

export interface GanttHeaderCell {
  x: number;
  width: number;
  label: string;
}

export interface GanttRow {
  kind: "group" | "task";
  /** Index into the tasks, for task rows. */
  task: number | null;
  /** Group rows: index of the group in order of appearance. */
  group: number;
  label: string;
  y: number;
  /** Bar or summary bracket; a milestone's diamond centre is `bar.x`. */
  bar: Rect | null;
  progress: number | null;
  milestone: boolean;
}

export interface GanttArrow {
  from: number;
  to: number;
  points: [number, number][];
  /** The successor starts before its predecessor ends. */
  early: boolean;
}

export interface GanttLayout {
  width: number;
  height: number;
  title: TitleBlock | null;
  /** Left edge of the time area and the first day on it. */
  originX: number;
  firstDay: number;
  /** Day after the last day on show. */
  lastDay: number;
  dayWidth: number;
  /** Top and bottom of the rows (for gridlines and the today line). */
  top: number;
  bottom: number;
  labelWidth: number;
  headerTop: number;
  upper: GanttHeaderCell[];
  lower: GanttHeaderCell[];
  /** x positions of vertical gridlines. */
  grid: number[];
  weekendShade: Rect[];
  rows: GanttRow[];
  arrows: GanttArrow[];
  groups: string[];
}

export const GANTT_ROW = 26;
const BAR_HEIGHT = 14;
const HEADER_ROW = 18;
const LABEL_FONT = 12;
const DAY_WIDTH: Record<GanttScale, number> = { day: 22, week: 5, month: 2 };
const MAX_LABEL_WIDTH = 220;
const MIN_WIDTH = 640;

function visibleRange(
  tasks: readonly GanttTask[],
  scale: GanttScale
): [number, number] {
  const first = Math.min(...tasks.map((task) => task.start));
  const last = Math.max(
    ...tasks.map((task) => Math.max(task.end, task.start + 1))
  );
  if (scale === "day") {
    return [first - 1, last + 1];
  }
  if (scale === "week") {
    return [startOfWeek(first), startOfWeek(last - 1) + 7];
  }
  return [monthStart(monthIndex(first)), monthStart(monthIndex(last - 1) + 1)];
}

interface Segment {
  start: number;
  end: number;
}

function monthSegments(first: number, last: number): Segment[] {
  const segments: Segment[] = [];
  for (let index = monthIndex(first); monthStart(index) < last; index++) {
    segments.push({
      start: Math.max(first, monthStart(index)),
      end: Math.min(last, monthStart(index + 1)),
    });
  }
  return segments;
}

function yearSegments(first: number, last: number): Segment[] {
  const segments: Segment[] = [];
  for (let year = civilDate(first).year; monthStart(year * 12) < last; year++) {
    segments.push({
      start: Math.max(first, monthStart(year * 12)),
      end: Math.min(last, monthStart((year + 1) * 12)),
    });
  }
  return segments;
}

function stepSegments(first: number, last: number, step: number): Segment[] {
  const segments: Segment[] = [];
  for (let day = first; day < last; day += step) {
    segments.push({ start: day, end: Math.min(last, day + step) });
  }
  return segments;
}

function headerCells(
  segments: readonly Segment[],
  unit: GanttHeaderUnit,
  x: (day: number) => number,
  label: GanttHeaderLabel
): GanttHeaderCell[] {
  return segments.map((segment) => {
    const left = x(segment.start);
    const width = x(segment.end) - left;
    return {
      x: left,
      width,
      label: width >= 8 ? label(unit, segment.start, width - 6) : "",
    };
  });
}

/** Rows in order: tasks without a group first, then each group's header and tasks. */
function orderRows(tasks: readonly GanttTask[]): {
  groups: string[];
  order: (number | string)[];
} {
  const groups: string[] = [];
  for (const task of tasks) {
    if (task.group !== "" && !groups.includes(task.group)) {
      groups.push(task.group);
    }
  }
  const order: (number | string)[] = tasks
    .map((task, index) => (task.group === "" ? index : -1))
    .filter((index) => index >= 0);
  for (const group of groups) {
    order.push(group);
    for (const [index, task] of tasks.entries()) {
      if (task.group === group) {
        order.push(index);
      }
    }
  }
  return { groups, order };
}

/** The elbow route of a finish-to-start arrow from one bar's end to another's start. */
export function dependencyRoute(
  from: { x: number; y: number },
  to: { x: number; y: number }
): [number, number][] {
  if (to.x - from.x >= 14) {
    const turn = from.x + 7;
    return [
      [from.x, from.y],
      [turn, from.y],
      [turn, to.y],
      [to.x, to.y],
    ];
  }
  const between = to.y > from.y ? to.y - GANTT_ROW / 2 : to.y + GANTT_ROW / 2;
  return [
    [from.x, from.y],
    [from.x + 7, from.y],
    [from.x + 7, between],
    [to.x - 9, between],
    [to.x - 9, to.y],
    [to.x, to.y],
  ];
}

function rowsFor(
  tasks: readonly GanttTask[],
  x: (day: number) => number,
  top: number
): { rows: GanttRow[]; groups: string[] } {
  const { groups, order } = orderRows(tasks);
  const rows = order.map((entry, position): GanttRow => {
    const y = top + position * GANTT_ROW;
    if (typeof entry === "string") {
      const members = tasks.filter((task) => task.group === entry);
      const start = Math.min(...members.map((task) => task.start));
      const end = Math.max(
        ...members.map((task) => Math.max(task.end, task.start))
      );
      return {
        kind: "group",
        task: null,
        group: groups.indexOf(entry),
        label: entry,
        y,
        bar: {
          x: x(start),
          y: y + GANTT_ROW / 2 - 3,
          width: Math.max(2, x(end) - x(start)),
          height: 6,
        },
        progress: null,
        milestone: false,
      };
    }
    const task = tasks[entry];
    return {
      kind: "task",
      task: entry,
      group: task.group === "" ? -1 : groups.indexOf(task.group),
      label: task.name,
      y,
      bar: {
        x: x(task.start),
        y: y + (GANTT_ROW - BAR_HEIGHT) / 2,
        width: task.milestone ? 0 : Math.max(2, x(task.end) - x(task.start)),
        height: BAR_HEIGHT,
      },
      progress: task.progress,
      milestone: task.milestone,
    };
  });
  return { rows, groups };
}

function arrowsFor(
  tasks: readonly GanttTask[],
  rows: readonly GanttRow[]
): GanttArrow[] {
  const rowOf = new Map<number, GanttRow>();
  for (const row of rows) {
    if (row.task !== null) {
      rowOf.set(tasks[row.task].number, row);
    }
  }
  const arrows: GanttArrow[] = [];
  for (const task of tasks) {
    const target = rowOf.get(task.number);
    for (const on of task.depends) {
      const source = rowOf.get(on);
      const before = tasks[on - 1];
      if (!(source?.bar && target?.bar && before)) {
        continue;
      }
      const fromX = source.milestone
        ? source.bar.x + 7
        : source.bar.x + source.bar.width;
      const toX = target.milestone ? target.bar.x - 7 : target.bar.x;
      arrows.push({
        from: on,
        to: task.number,
        points: dependencyRoute(
          { x: fromX, y: source.y + GANTT_ROW / 2 },
          { x: toX, y: target.y + GANTT_ROW / 2 }
        ),
        early: task.start < before.end,
      });
    }
  }
  return arrows;
}

/** Plans longer than this are almost always a mistyped year, and too wide to draw. */
export const GANTT_MAX_YEARS = 30;

/** True when the tasks span more than `GANTT_MAX_YEARS` years. */
export function ganttTooLong(tasks: readonly GanttTask[]): boolean {
  if (tasks.length === 0) {
    return false;
  }
  const first = Math.min(...tasks.map((task) => task.start));
  const last = Math.max(...tasks.map((task) => task.end));
  return last - first > GANTT_MAX_YEARS * 366;
}

/** Lay out a Gantt chart. Expects at least one task. */
export function layoutGantt(input: GanttLayoutInput): GanttLayout {
  const { tasks, scale } = input;
  const [firstDay, lastDay] = visibleRange(tasks, scale);
  const days = lastDay - firstDay;
  const taskStyle = { fontSize: LABEL_FONT };
  const groupStyle = { fontSize: LABEL_FONT, bold: true };
  const widest = Math.max(
    80,
    ...tasks.map((task) => estimateTextWidth(task.name, taskStyle) + 12),
    ...tasks.map((task) => estimateTextWidth(task.group, groupStyle))
  );
  const labelWidth = Math.ceil(Math.min(MAX_LABEL_WIDTH, widest + 14));
  const natural = 2 * CHART_PADDING + labelWidth + days * DAY_WIDTH[scale];
  const width = Math.ceil(Math.max(MIN_WIDTH, natural));
  // A short project is stretched to the minimum width rather than left in a corner.
  const dayWidth = (width - 2 * CHART_PADDING - labelWidth) / days;
  const title = layoutTitle(input.title, width);
  const originX = CHART_PADDING + labelWidth;
  const x = (day: number) => originX + (day - firstDay) * dayWidth;
  const headerTop = CHART_PADDING + (title?.height ?? 0);
  const top = headerTop + 2 * HEADER_ROW + 6;
  const upperSegments =
    scale === "month"
      ? yearSegments(firstDay, lastDay)
      : monthSegments(firstDay, lastDay);
  let lowerSegments: Segment[] = stepSegments(firstDay, lastDay, 1);
  if (scale === "week") {
    lowerSegments = stepSegments(firstDay, lastDay, 7);
  } else if (scale === "month") {
    lowerSegments = monthSegments(firstDay, lastDay);
  }
  const upper = headerCells(
    upperSegments,
    scale === "month" ? "year" : "month",
    x,
    input.headerLabel
  );
  const lower = headerCells(
    lowerSegments,
    scale === "month" ? "monthOnly" : "day",
    x,
    input.headerLabel
  );
  const { rows, groups } = rowsFor(tasks, x, top);
  const bottom = top + rows.length * GANTT_ROW;
  const weekendShade: Rect[] = [];
  if (input.weekends && scale !== "month") {
    for (let day = firstDay; day < lastDay; day++) {
      if (isWeekend(day)) {
        weekendShade.push({
          x: x(day),
          y: top,
          width: dayWidth,
          height: bottom - top,
        });
      }
    }
  }
  return {
    width,
    height: Math.ceil(bottom + CHART_PADDING),
    title,
    originX,
    firstDay,
    lastDay,
    dayWidth,
    top,
    bottom,
    labelWidth,
    headerTop,
    upper,
    lower,
    grid: lower.map((cell) => cell.x),
    weekendShade,
    rows: rows.map((row) => ({
      ...row,
      label:
        row.kind === "group"
          ? truncateText(row.label, labelWidth - 10, groupStyle)
          : truncateText(
              row.label,
              labelWidth - (row.group >= 0 ? 22 : 10),
              taskStyle
            ),
    })),
    arrows: arrowsFor(tasks, rows),
    groups,
  };
}
